import { Availability } from '@gql-generated/graphql'
import type { ProductAvailabilityIssue } from '../components/product-availability-issue-card/ProductAvailabilityIssueCard.client'
import { AVAILABILITY_ISSUES } from '../components/product-availability-issue-card/ProductAvailabilityIssueCard.client'
import { shouldDisplayProductPrices } from './productAvailabilities'

/** Minimal product shape required by getAvailabilityIssue and hasDisplayablePrice */
export type AvailabilityProduct = {
  bestArticlePrice?: number | null
  stock?: {
    availability?: Array<Availability | null> | null
    isPriceAvailable?: boolean | null
    isSellable?: boolean | null
  } | null
  canAddToCart?: boolean | null
  filters?: Array<unknown> | null
  nbArticles?: number | null
  price?: {
    exclTax?: number | null
    discountPercentage?: number | null
    discountExclTax?: number | null
  } | null
}

export function getAvailabilityIssue({
  product,
  isPublicPriceEnabled,
  isProduct,
  isAuthenticated,
}: {
  product: AvailabilityProduct
  isPublicPriceEnabled: boolean
  isProduct: boolean
  isAuthenticated: boolean
}): ProductAvailabilityIssue | null {
  // Product type with a bestArticlePrice: no availability issue (families 4–19 when isProduct)
  // @see https://descourscabaudnet.sharepoint.com/:x:/r/sites/DigitalFactory/_layouts/15/doc.aspx?sourcedoc=%7B7b7c714a-58b2-4228-a022-953996989352%7D
  if (isProduct && product.bestArticlePrice) {
    return null
  }

  // Cases 1_10: product not sellable — @see https://descourscabaudnet.sharepoint.com/:x:/r/sites/DigitalFactory/_layouts/15/doc.aspx?sourcedoc=%7B7b7c714a-58b2-4228-a022-953996989352%7D
  if (!product.stock?.isSellable) {
    return AVAILABILITY_ISSUES.IsNotSellable
  }

  // Cases 2_10: isPriceAvailable=false | also reached by disconnected guests on non-public-price sites
  // @see https://descourscabaudnet.sharepoint.com/:x:/r/sites/DigitalFactory/_layouts/15/doc.aspx?sourcedoc=%7B7b7c714a-58b2-4228-a022-953996989352%7D
  if (
    product.stock?.isPriceAvailable === false ||
    !shouldDisplayProductPrices({ product, isPublicPriceEnabled, isAuthenticated, isProduct })
  ) {
    return AVAILABILITY_ISSUES.NoPricesAvailable
  }

  // Cases 3_10, 3_20: availability=[STORE_ONLY] — @see https://descourscabaudnet.sharepoint.com/:x:/r/sites/DigitalFactory/_layouts/15/doc.aspx?sourcedoc=%7B7b7c714a-58b2-4228-a022-953996989352%7D
  if (product.stock?.availability?.includes(Availability.StoreOnly)) {
    return AVAILABILITY_ISSUES.StoreOnly
  }

  // Cases 4+ (connected): no availability issue — @see https://descourscabaudnet.sharepoint.com/:x:/r/sites/DigitalFactory/_layouts/15/doc.aspx?sourcedoc=%7B7b7c714a-58b2-4228-a022-953996989352%7D
  return null
}

/**
 * Determines whether the product has a displayable price.
 * Combines price data availability with absence of availability issues.
 */
export function hasDisplayablePrice({
  product,
  isProduct,
  availabilityIssue,
}: {
  product: AvailabilityProduct
  isProduct: boolean
  availabilityIssue: ProductAvailabilityIssue | null
}): boolean {
  const hasBestArticlePrice = isProduct && (product.bestArticlePrice || 0) > 0
  const hasExclTaxPrice = (product.price?.exclTax || 0) > 0

  return (hasBestArticlePrice || hasExclTaxPrice) && !availabilityIssue
}

/**
 * Determines if the "Equivalent Products" link should be shown.
 * It is visible only when the product cannot be added to cart,
 * and there is no blocker such as NoPricesAvailable or IsNotSellable.
 */
export function canDisplayEquivalentProductsLink({
  canAddToCart,
  availabilityIssue,
}: {
  canAddToCart?: boolean | null
  availabilityIssue?: ProductAvailabilityIssue | null
}): boolean {
  return (
    !canAddToCart &&
    availabilityIssue !== AVAILABILITY_ISSUES.NoPricesAvailable &&
    availabilityIssue !== AVAILABILITY_ISSUES.IsNotSellable
  )
}
